/**
 * 送れていない録音の出し入れと送り直し（PRODUCT_DEV-1270・フラグ offlineRecordingSave）
 *
 * - 録音画面は送る前に `savePendingRecording` で端末に入れ、`sendPendingRecording` で送る
 * - 画面全体に置いた `PendingRecordingsBanner` が、つながったとき・画面に戻ったとき・開いたときに
 *   `sendAllPending` で残りを送る
 * - 同じ録音を2か所（録音画面と帯・別のタブ）から同時に送らないよう、録音ごとに鍵をかける
 */
import { apiPost } from "@shared-ui/utils/api";
import { buildUrl } from "@shared-ui/utils/url";
import {
  API_MEMOS_AUDIO_UPLOAD_URL,
  API_MEMOS_MEMOID_TRANSCRIBE,
  API_SPEED_RECORDING_SUBMIT,
  API_SPEED_RECORDING_UPLOAD_URL,
  API_MEETINGS,
  API_MINUTES_MINUTESID_CHUNK_UPLOAD_URL,
  API_MINUTES_MINUTESID_FINALIZE,
  API_MINUTES_MINUTESID_HEARTBEAT,
} from "@/utils/const";
import {
  deletePending,
  getPending,
  listPending,
  putPending,
  putPendingIfExists,
  requestPersistentStorage,
} from "./pendingStore";
import { sendPending, type SendDeps, type SendEndpoints } from "./sendPending";
import type { PendingKind, PendingRecording, SendOutcome } from "./types";

/** 端末に残っている録音を全部消す（ログアウトの前に、人が消すと決めたとき） */
export async function discardAllPending(): Promise<void> {
  for (const p of await listPending()) await deletePending(p.id);
  notifyChanged();
}

export const ENDPOINTS: Record<PendingKind, SendEndpoints> = {
  speed: {
    uploadUrl: API_SPEED_RECORDING_UPLOAD_URL,
    serverIdField: "meetingId",
    submit: (meetingId, item) => ({
      url: API_SPEED_RECORDING_SUBMIT,
      body: { meetingId, tenantId: item.tenantId, durationSec: item.durationSec },
    }),
  },
  dialogue: {
    single: {
      url: API_MEETINGS,
      idOf: (res) => (res as { meeting?: { id?: string } } | null)?.meeting?.id,
    },
    uploadUrl: API_MEETINGS,
    serverIdField: "meetingId",
    submit: () => ({ url: API_MEETINGS, body: {} }),
  },
  minutesChunk: {
    chunk: {
      url: (item) =>
        buildUrl(API_MINUTES_MINUTESID_CHUNK_UPLOAD_URL, { minutesId: String(item.request.minutesId) }),
      body: (item) => ({
        seq: item.request.seq,
        contentType: item.baseContentType,
        extension: item.extension,
        tenantId: item.tenantId,
      }),
    },
    uploadUrl: API_MINUTES_MINUTESID_CHUNK_UPLOAD_URL,
    serverIdField: "meetingId",
    submit: () => ({ url: API_MINUTES_MINUTESID_CHUNK_UPLOAD_URL, body: {} }),
  },
  minutesFinalize: {
    post: {
      url: (item) => buildUrl(API_MINUTES_MINUTESID_FINALIZE, { minutesId: String(item.request.minutesId) }),
      // totalChunks は送る直前に数え直して request に入れる（sendPendingRecording）
      body: (item) => ({
        totalChunks: item.request.totalChunks,
        durationSec: item.request.durationSec,
        tenantId: item.tenantId,
      }),
    },
    uploadUrl: API_MINUTES_MINUTESID_FINALIZE,
    serverIdField: "meetingId",
    submit: () => ({ url: API_MINUTES_MINUTESID_FINALIZE, body: {} }),
  },
  memo: {
    uploadUrl: API_MEMOS_AUDIO_UPLOAD_URL,
    serverIdField: "memoId",
    submit: (memoId, item) => ({
      url: buildUrl(API_MEMOS_MEMOID_TRANSCRIBE, { memoId }),
      body: { tenantId: item.tenantId, durationSec: item.durationSec },
    }),
  },
};

const deps: SendDeps = {
  apiPost: (url, body) => apiPost(url, body),
  // 送り先の署名と Content-Type は完全に一致させる（最初の送信と同じ）
  putAudio: (url, item) =>
    fetch(url, { method: "PUT", headers: { "Content-Type": item.baseContentType }, body: item.blob }),
  save: (item) => putPendingIfExists(item),
};

/** 件数が変わったことを帯に知らせる */
export const PENDING_CHANGED_EVENT = "unizo:pending-recordings-changed";
const notifyChanged = () => {
  try {
    window.dispatchEvent(new Event(PENDING_CHANGED_EVENT));
  } catch {
    // 知らせられなくても、帯は次に開いたとき数え直す
  }
};

const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** 送る前に端末へ入れる。入れられなかったら null（そのときは今までどおり送るだけ） */
export async function savePendingRecording(
  input: Omit<PendingRecording, "id" | "createdAt" | "stage" | "attempts">,
): Promise<PendingRecording | null> {
  const item: PendingRecording = {
    ...input,
    id: newId(),
    createdAt: new Date().toISOString(),
    stage: "new",
    attempts: 0,
  };
  if (!(await putPending(item))) return null;
  void requestPersistentStorage();
  notifyChanged();
  return item;
}

// 同じタブの中で送っている最中の録音
const inFlight = new Set<string>();

/** 録音ごとの鍵（別のタブとも）。取れなければ誰かが送っている最中なので何もしない */
type LockedOutcome = SendOutcome | { kind: "gone" } | { kind: "skipped" };

const withLock = async (
  id: string,
  fn: () => Promise<LockedOutcome>,
): Promise<LockedOutcome | null> => {
  if (inFlight.has(id)) return null;
  inFlight.add(id);
  try {
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (!locks?.request) return await fn();
    return await locks.request(`unizo-pending-recording-${id}`, { ifAvailable: true }, async (lock) =>
      lock ? fn() : null,
    );
  } finally {
    inFlight.delete(id);
  }
};

/**
 * 1件送る。送れたら端末から消す。通らないものには failed の印を付けて残す。
 * 鍵を取ったあとに端末から読み直し、その最新の段から送る（別のタブが送り終えた・途中まで進めたものを、
 * 古い控えから送って記録を二重に作らないため）。端末から消えていたら何もしない
 */
export async function sendPendingRecording(
  item: Pick<PendingRecording, "id">,
  { retryFailed = false }: { retryFailed?: boolean } = {},
): Promise<SendOutcome | null> {
  const outcome = await withLock(item.id, async () => {
    const latest = await getPending(item.id);
    if (!latest) return { kind: "gone" } as const;
    if (latest.draft) return { kind: "skipped" } as const;
    if (latest.failed && !retryFailed) return { kind: "skipped" } as const;
    // 議事録の確定は、その議事録の区切りを全部送り終えてから（届く数を数え直して送る）
    if (latest.kind === "minutesFinalize") {
      const ready = await finalizeRequestWhenChunksSent(latest);
      if (!ready) return { kind: "retry" } as const;
      return sendPending({ ...ready, failed: false }, ENDPOINTS.minutesFinalize, deps);
    }
    return sendPending({ ...latest, failed: false }, ENDPOINTS[latest.kind], deps);
  });
  if (!outcome || outcome.kind === "skipped") return null;
  if (outcome.kind === "gone") {
    notifyChanged();
    return null;
  }
  if (outcome.kind === "done") {
    await deletePending(item.id);
  } else if (outcome.kind === "failed") {
    const latest = await getPending(item.id);
    if (latest) await putPending({ ...latest, failed: true });
  }
  notifyChanged();
  return outcome;
}

/** この人・この会社の送れていない録音（古い順） */
export async function listMyPending(tenantId: string, userId: string): Promise<PendingRecording[]> {
  return (await listPending())
    .filter((p) => p.tenantId === tenantId && p.userId === userId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * PRODUCT_DEV-1342: 端末に残す期限。録った日から 7 日（2026-11-05 ユーザ判断）。
 * 過ぎた控えは送っても使えない（議事録の区切りはサーバで 3 日・自由記録の出し直しは 20 分で終わる）うえ、
 * 録音とお客さまの情報が共有端末に残り続けるので、帯で知らせて消す。
 * 別の人の控え（違う会社・違う人でログインしている端末に残ったもの）は、その人が戻れば送れるので消さない。
 * サーバ側の音声メモの送り直しの期限（unizo API `memos/audio-upload-url` の MEMO_REISSUE_MAX_AGE_MS）と同じ 7 日にそろえている
 */
export const PENDING_RETENTION_DAYS = 7;
export const PENDING_RETENTION_MS = PENDING_RETENTION_DAYS * 24 * 60 * 60 * 1000;

export const isExpiredPending = (p: Pick<PendingRecording, "createdAt">, nowMs: number): boolean => {
  const created = Date.parse(p.createdAt);
  return !Number.isFinite(created) || nowMs - created >= PENDING_RETENTION_MS;
};

/**
 * 期限を過ぎた、この人・この会社の控えを消す。消した件数（記録ごと）と、残った控え（listMyPending と同じ並び）を返す
 * （帯が一覧をもう一度読まなくて済むように）
 */
export async function purgeExpiredPending(
  tenantId: string,
  userId: string,
  nowMs: number = Date.now(),
): Promise<{ purgedCount: number; remaining: PendingRecording[] }> {
  const mine = await listMyPending(tenantId, userId);
  const expired = mine.filter((p) => isExpiredPending(p, nowMs));
  if (expired.length === 0) return { purgedCount: 0, remaining: mine };
  await Promise.all(expired.map((p) => deletePending(p.id)));
  notifyChanged();
  return { purgedCount: countRecordings(expired), remaining: mine.filter((p) => !isExpiredPending(p, nowMs)) };
}

/** 件数は記録ごと（議事録の区切りと確定は、同じ議事録のものを1件と数える） */
export const countRecordings = (items: readonly Pick<PendingRecording, "id" | "kind" | "request">[]): number =>
  new Set(
    items.map((i) =>
      i.kind === "minutesChunk" || i.kind === "minutesFinalize" ? `minutes:${String(i.request.minutesId)}` : i.id,
    ),
  ).size;

/**
 * 残りをまとめて送る。`includeFailed` は人が「今すぐ送る」を押したときだけ（自動では通らないものを送らない）。
 * 送れた件数を返す
 */
export async function sendAllPending(
  tenantId: string,
  userId: string,
  { includeFailed = false }: { includeFailed?: boolean } = {},
): Promise<number> {
  let sent = 0;
  const minutesDone = new Set<string>();
  for (const item of await listMyPending(tenantId, userId)) {
    // 議事録の区切りと確定は議事録ごとにまとめて送る（生存確認を送り、区切りを送り終えてから確定）
    if (item.kind === "minutesChunk" || item.kind === "minutesFinalize") {
      const minutesId = String(item.request.minutesId);
      if (minutesDone.has(minutesId)) continue;
      minutesDone.add(minutesId);
      const before = (await listMyPending(tenantId, userId)).length;
      await resendMinutesChunks(tenantId, userId, minutesId, { byHand: includeFailed });
      sent += Math.max(0, before - (await listMyPending(tenantId, userId)).length);
      continue;
    }
    if (item.failed && !includeFailed) continue;
    // 対話の控えは送らない（人が「そこまでで保存」を選んでから）
    if (item.draft) continue;
    const outcome = await sendPendingRecording(item, { retryFailed: includeFailed });
    if (outcome?.kind === "done") sent += 1;
    // 通信が切れているなら、残りを試しても同じなのでやめる
    if (outcome?.kind === "retry") break;
  }
  return sent;
}

export async function discardPendingRecording(id: string): Promise<void> {
  const item = await getPending(id);
  await deletePending(id);
  // 議事録の区切りを捨てたら、その議事録の確定で伝える数から引く（届かないので）
  if (item?.kind === "minutesChunk") {
    const fin = (await listPending()).find(
      (p) => p.kind === "minutesFinalize" && p.request.minutesId === item.request.minutesId,
    );
    if (fin) {
      await putPending({
        ...fin,
        request: { ...fin.request, discarded: Number(fin.request.discarded ?? 0) + 1 },
      });
    }
  }
  notifyChanged();
}

// ---- 対話モード（PRODUCT_DEV-1270 第2段） ----

// 同じ控えへの書き込みを順に並べる（読んでから書くまでの間に、保存が控えを送るものに変えたり
// 送れて消したりしても、書き戻さないため。同じタブの中だけ）
const draftQueue = new Map<string, Promise<unknown>>();
const inDraftQueue = <T>(id: string, fn: () => Promise<T>): Promise<T> => {
  const prev = draftQueue.get(id) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  draftQueue.set(
    id,
    next.catch(() => undefined).finally(() => {
      if (draftQueue.get(id) === next) draftQueue.delete(id);
    }),
  );
  return next;
};

/** 対話の控えを書かなくなってから「途中で終わった」とみなすまでの時間（録音画面は数秒ごとに書く） */
export const DRAFT_STALE_MS = 60_000;

const draftItem = (
  id: string,
  request: Record<string, unknown>,
  owner: { tenantId: string; userId: string },
  draft: boolean,
  createdAt?: string,
): PendingRecording => ({
  id,
  kind: "dialogue",
  tenantId: owner.tenantId,
  userId: owner.userId,
  createdAt: createdAt ?? new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  blob: new Blob([]),
  baseContentType: "",
  extension: "",
  durationSec: 0,
  request,
  stage: "new",
  attempts: 0,
  draft,
});

/**
 * 録音中の対話の控えを書く（同じ id に上書き）。書けたら id を返す。
 * 控えは送らない（録音画面が終わるか、落ちたまま時間がたったときに扱う）
 */
export async function writeDialogueDraft(
  id: string | null,
  request: Record<string, unknown>,
  owner: { tenantId: string; userId: string },
): Promise<string | null> {
  if (id) return inDraftQueue(id, () => writeDraftNow(id, request, owner));
  return writeDraftNow(null, request, owner);
}

async function writeDraftNow(
  id: string | null,
  request: Record<string, unknown>,
  owner: { tenantId: string; userId: string },
): Promise<string | null> {
  const prev = id ? await getPending(id) : null;
  // 保存で送るものに変わった・送れて消えた控えは書き戻さない（書いている途中で保存が終わったとき）
  if (id && (!prev || !prev.draft)) return null;
  const next = draftItem(id ?? newId(), request, owner, true, prev?.createdAt);
  if (!(await putPending(next))) return null;
  if (!prev) {
    void requestPersistentStorage();
    notifyChanged();
  }
  return next.id;
}

/** 保存の中身を、送るものとして端末に入れる（控えがあればそれを置き換える） */
export async function saveDialogueForResend(
  request: Record<string, unknown>,
  owner: { tenantId: string; userId: string; draftId?: string | null },
): Promise<PendingRecording | null> {
  // 書いている途中の控えを待ってから置き換える（あとから来た書き込みは draft でなくなったので止まる）
  if (owner.draftId) return inDraftQueue(owner.draftId, () => saveNow(request, owner));
  return saveNow(request, owner);
}

async function saveNow(
  request: Record<string, unknown>,
  owner: { tenantId: string; userId: string; draftId?: string | null },
): Promise<PendingRecording | null> {
  const id = owner.draftId ?? newId();
  const prev = owner.draftId ? await getPending(owner.draftId) : null;
  const item = draftItem(id, request, owner, false, prev?.createdAt);
  if (!(await putPending(item))) return null;
  void requestPersistentStorage();
  notifyChanged();
  return item;
}

/** 録音中の控えを捨てる（保存できた・やめた） */
export async function discardDialogueDraft(id: string | null): Promise<void> {
  if (!id) return;
  await inDraftQueue(id, async () => {
    const prev = await getPending(id);
    if (prev?.draft) {
      await deletePending(id);
      notifyChanged();
    }
  });
}

/** 控えのうち、録音画面が書かなくなって時間がたったもの（アプリが落ちた・閉じた） */
export const isStaleDraft = (p: PendingRecording, nowMs: number): boolean =>
  !!p.draft && nowMs - Date.parse(p.updatedAt ?? p.createdAt) >= DRAFT_STALE_MS;

/** 途中で終わった対話の控えを、送るものに変える（人が「そこまでで保存」を押したとき） */
export async function promoteDraft(id: string): Promise<void> {
  const prev = await getPending(id);
  if (!prev) return;
  await putPending({ ...prev, draft: false });
  notifyChanged();
}

// ---- 議事録の区切り（PRODUCT_DEV-1270 第3段） ----

/** 区切りを送る前に端末へ入れる。入れられなかったら null（そのときは今までどおり送るだけ） */
export async function saveMinutesChunk(input: {
  tenantId: string;
  userId: string;
  minutesId: string;
  seq: number;
  blob: Blob;
  baseContentType: string;
  extension: string;
}): Promise<PendingRecording | null> {
  return savePendingRecording({
    kind: "minutesChunk",
    tenantId: input.tenantId,
    userId: input.userId,
    blob: input.blob,
    baseContentType: input.baseContentType,
    extension: input.extension,
    durationSec: 0,
    request: { minutesId: input.minutesId, seq: input.seq },
  });
}

/** この議事録の、まだ送れていない区切り（古い順） */
export async function listMinutesChunks(
  tenantId: string,
  userId: string,
  minutesId: string,
): Promise<PendingRecording[]> {
  return (await listMyPending(tenantId, userId)).filter(
    (p) => p.kind === "minutesChunk" && p.request.minutesId === minutesId,
  );
}

/** 区切りをこの数だけ送るごとに、生存確認を送り直す */
const HEARTBEAT_EVERY_CHUNKS = 5;

/**
 * この議事録の送れていない区切りを送り直し、全部送れたら確定も送る（録音中・つながったとき・画面から）。
 * byHand（人が押した）なら、通らなかった区切りも送り直す。残った数（区切り＋確定）を返す
 */
export async function resendMinutesChunks(
  tenantId: string,
  userId: string,
  minutesId: string,
  { byHand = false }: { byHand?: boolean } = {},
): Promise<number> {
  const chunks = await listMinutesChunks(tenantId, userId, minutesId);
  // 送り直す前に生存確認を送る。片付け（生存確認が10分途絶えたら届いた分で確定）が、送っている途中に
  // 届いた1つだけで確定して、短い録音を消してしまわないように（届かなくても続ける）
  if (chunks.length > 0) {
    await apiPost(buildUrl(API_MINUTES_MINUTESID_HEARTBEAT, { minutesId }), { tenantId }).catch(
      () => undefined,
    );
  }
  let sinceBeat = 0;
  for (const item of chunks) {
    if (item.failed && !byHand) continue;
    // 区切りが多いときは、送っている間も生存確認を送り直す（回線が遅くて10分を超えないように）
    if (++sinceBeat > HEARTBEAT_EVERY_CHUNKS) {
      sinceBeat = 1;
      await apiPost(buildUrl(API_MINUTES_MINUTESID_HEARTBEAT, { minutesId }), { tenantId }).catch(
        () => undefined,
      );
    }
    const outcome = await sendPendingRecording(item, { retryFailed: byHand });
    if (outcome?.kind === "retry") break;
  }
  const fin = (await listMyPending(tenantId, userId)).find(
    (p) => p.kind === "minutesFinalize" && p.request.minutesId === minutesId,
  );
  if (fin) await sendPendingRecording(fin, { retryFailed: byHand });
  return (await listMyPending(tenantId, userId)).filter(
    (p) =>
      (p.kind === "minutesChunk" || p.kind === "minutesFinalize") && p.request.minutesId === minutesId,
  ).length;
}

/**
 * 区切りを送り切れなかった議事録の確定を、端末に残す（区切りを全部送り終えたら送る）。
 * producedCount は録音で作った区切りの数
 */
export async function saveMinutesFinalize(input: {
  tenantId: string;
  userId: string;
  minutesId: string;
  producedCount: number;
  durationSec: number;
}): Promise<PendingRecording | null> {
  return savePendingRecording({
    kind: "minutesFinalize",
    tenantId: input.tenantId,
    userId: input.userId,
    blob: new Blob([]),
    baseContentType: "",
    extension: "",
    durationSec: input.durationSec,
    request: {
      minutesId: input.minutesId,
      producedCount: input.producedCount,
      durationSec: input.durationSec,
      discarded: 0,
    },
  });
}

/**
 * 確定を送ってよければ、届く区切りの数（作った数 − 捨てた数 − まだ届いていない数）を入れた中身を返す。
 * 送れていない区切りがまだあるなら null（あとで）。通らなかった区切り（failed）は届かないものとして数える
 */
async function finalizeRequestWhenChunksSent(
  fin: PendingRecording,
): Promise<PendingRecording | null> {
  const chunks = await listMinutesChunks(fin.tenantId, fin.userId, String(fin.request.minutesId));
  if (chunks.some((c) => !c.failed)) return null;
  const totalChunks = Math.max(
    0,
    Number(fin.request.producedCount ?? 0) - Number(fin.request.discarded ?? 0) - chunks.length,
  );
  return { ...fin, request: { ...fin.request, totalChunks } };
}
